import { useState, useEffect, useCallback, useRef } from 'react';
import { getJobsByLeadId } from '../api/jobs-api';
import type { JobListItemDto } from '../types';

/**
 * Hook for fetching all jobs for a specific lead.
 * Uses AbortController to cancel in-flight requests when leadId changes or on unmount.
 */
export function useJobsByLead(leadId: number | undefined) {
  const [data, setData] = useState<JobListItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchJobs = useCallback(async () => {
    // Cancel any in-flight request
    abortControllerRef.current?.abort();

    if (!leadId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const jobs = await getJobsByLeadId(leadId, controller.signal);
      if (!controller.signal.aborted) {
        setData(jobs);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (controller.signal.aborted) return;
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch jobs for lead');
      setData([]);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [leadId]);

  useEffect(() => {
    fetchJobs();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchJobs]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchJobs,
  };
}
